// SPDX-FileCopyrightText: 2025 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0

/**
 * Custom Pino OTEL Transport
 *
 * Standalone exporter for Pino logs to LiveKit Cloud.
 * Uses raw HTTP JSON format, bypassing the OTEL SDK.
 */
import { SeverityNumber } from '@opentelemetry/api-logs';
import { AccessToken } from 'livekit-server-sdk';
import { ATTRIBUTE_REDACTION_ENABLED } from '../types.js';
import { resolveObservabilityUrl } from './observability_endpoint.js';
import { REDACTED_EXCEPTION_MESSAGE, isPIIAttribute } from './redaction.js';
import { fetchWithUploadGate, uploadGate } from './upload_gate.js';

export interface PinoLogObject {
  level: number;
  time: number;
  msg: string;
  pid?: number;
  hostname?: string;
  [key: string]: unknown;
}

export interface PinoCloudExporterConfig {
  /** @deprecated Pass `observabilityUrl` with `PinoCloudExporterUrlConfig`. */
  cloudHostname: string;
  roomId: string;
  jobId: string;
  metadata?: Record<string, unknown>;
  loggerName?: string;
  batchSize?: number;
  flushIntervalMs?: number;
}

export interface PinoCloudExporterUrlConfig {
  /** Base URL for LiveKit Cloud observability, without a trailing slash. */
  observabilityUrl: string;
  roomId: string;
  jobId: string;
  metadata?: Record<string, unknown>;
  loggerName?: string;
  batchSize?: number;
  flushIntervalMs?: number;
}

function mapPinoLevelToSeverity(pinoLevel: number): {
  severityNumber: SeverityNumber;
  severityText: string;
} {
  if (pinoLevel <= 10) {
    return { severityNumber: SeverityNumber.TRACE, severityText: 'TRACE' };
  } else if (pinoLevel <= 20) {
    return { severityNumber: SeverityNumber.DEBUG, severityText: 'DEBUG' };
  } else if (pinoLevel <= 30) {
    return { severityNumber: SeverityNumber.INFO, severityText: 'INFO' };
  } else if (pinoLevel <= 40) {
    return { severityNumber: SeverityNumber.WARN, severityText: 'WARN' };
  } else if (pinoLevel <= 50) {
    return { severityNumber: SeverityNumber.ERROR, severityText: 'ERROR' };
  } else {
    return { severityNumber: SeverityNumber.FATAL, severityText: 'FATAL' };
  }
}

const EXCLUDE_FIELDS = new Set(['level', 'time', 'msg', 'pid', 'hostname', 'v']);

function convertValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return { stringValue: '' };
  }
  if (typeof value === 'string') {
    return { stringValue: value };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? { intValue: String(value) } : { doubleValue: value };
  }
  if (typeof value === 'boolean') {
    return { boolValue: value };
  }
  if (typeof value === 'object') {
    return { stringValue: JSON.stringify(value) };
  }
  return { stringValue: String(value) };
}

function redactSerializedException(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const exception = value as Record<string, unknown>;
  if (typeof exception.type !== 'string' || typeof exception.message !== 'string') {
    return value;
  }

  return {
    type: exception.type,
    message: REDACTED_EXCEPTION_MESSAGE,
  };
}

/**
 * Standalone Pino log exporter for LiveKit Cloud.
 *
 * Collects Pino logs, batches them, and sends via raw HTTP JSON.
 * No OTEL SDK dependency
 *
 * @example
 * ```typescript
 * const exporter = new PinoCloudExporter({
 *   observabilityUrl: 'https://cloud.livekit.io',
 *   roomId: 'RM_xxx',
 *   jobId: 'AJ_xxx',
 * });
 *
 * // In Pino formatter hook:
 * exporter.emit(logObj);
 *
 * // On session end:
 * await exporter.flush();
 * ```
 */
export class PinoCloudExporter {
  private readonly config: PinoCloudExporterConfig | PinoCloudExporterUrlConfig;
  private readonly loggerName: string;
  private readonly batchSize: number;
  private readonly flushIntervalMs: number;
  private jwt: string | null = null;
  private pendingLogs: any[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private flushChain: Promise<void> = Promise.resolve();

  constructor(config: PinoCloudExporterConfig | PinoCloudExporterUrlConfig) {
    this.config = config;
    this.loggerName = config.loggerName || 'livekit.agents';
    this.batchSize = config.batchSize || 100;
    this.flushIntervalMs = config.flushIntervalMs || 5000;
  }

  emit(logObj: PinoLogObject): void {
    const record = this.convertToOtlpRecord(logObj);
    this.pendingLogs.push(record);

    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flush().catch(console.error);
      }, this.flushIntervalMs);
    }

    if (this.pendingLogs.length >= this.batchSize) {
      this.flush().catch(console.error);
    }
  }

  private convertToOtlpRecord(logObj: PinoLogObject): any {
    const { severityNumber, severityText } = mapPinoLevelToSeverity(logObj.level);
    const redactionEnabled = this.config.metadata?.[ATTRIBUTE_REDACTION_ENABLED] === true;

    const attributes: any[] = [
      { key: 'room_id', value: { stringValue: this.config.roomId } },
      { key: 'job_id', value: { stringValue: this.config.jobId } },
      { key: 'logger.name', value: { stringValue: this.loggerName } },
    ];

    for (const [key, value] of Object.entries(this.config.metadata ?? {})) {
      attributes.push({ key, value: convertValue(value) });
    }

    if (logObj.pid !== undefined) {
      attributes.push({ key: 'process.pid', value: { intValue: String(logObj.pid) } });
    }
    if (logObj.hostname !== undefined) {
      attributes.push({ key: 'host.name', value: { stringValue: logObj.hostname } });
    }

    for (const [key, value] of Object.entries(logObj)) {
      if (EXCLUDE_FIELDS.has(key)) continue;
      // once the project mandates redaction the client filters the keys itself rather than
      // relying on the collector to know them
      if (redactionEnabled && isPIIAttribute(key)) continue;
      const attributeValue =
        redactionEnabled && (key === 'error' || key === 'err')
          ? redactSerializedException(value)
          : value;
      attributes.push({ key, value: convertValue(attributeValue) });
    }

    return {
      timeUnixNano: String(BigInt(logObj.time) * BigInt(1_000_000)),
      observedTimeUnixNano: String(BigInt(Date.now()) * BigInt(1_000_000)),
      severityNumber,
      severityText,
      body: { stringValue: logObj.msg || '' },
      attributes,
      traceId: '',
      spanId: '',
    };
  }

  flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const flush = this.flushChain.then(() => this.flushPendingLogs());
    this.flushChain = flush;
    return flush;
  }

  private async flushPendingLogs(): Promise<void> {
    while (this.pendingLogs.length > 0) {
      const logs = this.pendingLogs;
      this.pendingLogs = [];

      try {
        await this.sendLogs(logs);
      } catch (error) {
        this.pendingLogs = [...logs, ...this.pendingLogs];
        console.error('[PinoCloudExporter] Failed to flush logs:', error);
        return;
      }
    }
  }

  private async sendLogs(logRecords: any[]): Promise<void> {
    if (uploadGate.disabled) return;

    await this.ensureJwt();

    const payload = {
      resourceLogs: [
        {
          resource: {
            attributes: [
              { key: 'service.name', value: { stringValue: 'livekit-agents' } },
              { key: 'room_id', value: { stringValue: this.config.roomId } },
              { key: 'job_id', value: { stringValue: this.config.jobId } },
            ],
          },
          scopeLogs: [
            {
              scope: {
                name: this.loggerName,
                attributes: [
                  { key: 'room_id', value: { stringValue: this.config.roomId } },
                  { key: 'job_id', value: { stringValue: this.config.jobId } },
                  ...Object.entries(this.config.metadata ?? {}).map(([key, value]) => ({
                    key,
                    value: convertValue(value),
                  })),
                ],
              },
              logRecords,
            },
          ],
        },
      ],
    };

    const endpoint = `${resolveObservabilityUrl(this.config)}/observability/logs/otlp/v0`;

    const response = await fetchWithUploadGate(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await response.body?.cancel().catch(() => undefined);
      throw new Error(`Log export failed: status ${response.status}`);
    }
  }

  private async ensureJwt(): Promise<void> {
    if (this.jwt) return;

    const apiKey = process.env.LIVEKIT_API_KEY;
    const apiSecret = process.env.LIVEKIT_API_SECRET;

    if (!apiKey || !apiSecret) {
      throw new Error('LIVEKIT_API_KEY and LIVEKIT_API_SECRET must be set');
    }

    const token = new AccessToken(apiKey, apiSecret, { ttl: '6h' });
    token.addObservabilityGrant({ write: true });
    this.jwt = await token.toJwt();
  }

  async shutdown(): Promise<void> {
    await this.flush();
  }
}

let globalExporter: PinoCloudExporter | null = null;

export function initPinoCloudExporter(
  config: PinoCloudExporterConfig | PinoCloudExporterUrlConfig,
): void {
  globalExporter = new PinoCloudExporter(config);
}

export function getPinoCloudExporter(): PinoCloudExporter | null {
  return globalExporter;
}

export function emitToOtel(logObj: PinoLogObject): void {
  if (globalExporter) {
    globalExporter.emit(logObj);
  }
}

export async function flushPinoLogs(): Promise<void> {
  if (globalExporter) {
    await globalExporter.flush();
  }
}
